import { useLocaleState, useTranslate } from "ra-core";
import { useMemo } from "react";

import { useIsMobile } from "@/hooks/use-mobile";
import { MobileContent } from "../layout/MobileContent";
import MobileHeader from "../layout/MobileHeader";
import {
  AVANTE_CHANGELOG,
  CHANGELOG_TIME_ZONE,
  groupChangelogByDay,
  type ChangelogEntry,
} from "./avanteChangelog";
import { MobileBackButton } from "./MobileBackButton";
import "@/styles/list-skeleton.css";
import "@/styles/changelog.css";

/** "martes, 4 de noviembre de 2026" for a YYYY-MM-DD Madrid day. */
const useDayFormat = () => {
  const [locale = "es"] = useLocaleState();
  return useMemo(() => {
    const format = new Intl.DateTimeFormat(locale, {
      weekday: "long",
      day: "numeric",
      month: "long",
      year: "numeric",
      timeZone: CHANGELOG_TIME_ZONE,
    });
    // Noon UTC is the same calendar day in Madrid all year round.
    return (day: string) => format.format(new Date(`${day}T12:00:00Z`));
  }, [locale]);
};

const ChangelogCard = ({ entry }: { entry: ChangelogEntry }) => {
  const translate = useTranslate();
  return (
    <li className="changelog-card record-card">
      <div className="changelog-card__head">
        <h3 className="changelog-card__title">{entry.title}</h3>
        <span
          className="changelog-card__number"
          aria-label={translate("crm.changelog.change_number", {
            number: entry.pr,
          })}
        >
          #{entry.pr}
        </span>
      </div>
      <p className="changelog-card__text">{entry.description}</p>
      <ul
        className="changelog-card__tags"
        aria-label={translate("crm.changelog.categories_label")}
      >
        {entry.categories.map((category) => (
          <li key={category} className="changelog-tag" data-category={category}>
            {translate(`crm.changelog.categories.${category}`)}
          </li>
        ))}
      </ul>
    </li>
  );
};

const ChangelogTimeline = () => {
  const formatDay = useDayFormat();
  const days = useMemo(() => groupChangelogByDay(AVANTE_CHANGELOG), []);
  return (
    <div className="changelog">
      {days.map(({ day, entries }) => (
        <section
          key={day}
          className="changelog-day"
          aria-labelledby={`changelog-${day}`}
        >
          <h2 id={`changelog-${day}`} className="changelog-day__title">
            <time dateTime={day}>{formatDay(day)}</time>
          </h2>
          <ul className="changelog-day__list">
            {entries.map((entry) => (
              <ChangelogCard key={entry.pr} entry={entry} />
            ))}
          </ul>
        </section>
      ))}
    </div>
  );
};

export const ChangelogPage = () => {
  const translate = useTranslate();
  const isMobile = useIsMobile();

  if (isMobile) {
    return (
      <>
        <MobileHeader>
          <MobileBackButton to="/settings" />
          <div className="flex flex-1 min-w-0">
            <h1 className="text-xl font-semibold">
              {translate("crm.changelog.title")}
            </h1>
          </div>
        </MobileHeader>
        <MobileContent>
          <p className="mb-5 text-sm text-muted-foreground">
            {translate("crm.changelog.subtitle")}
          </p>
          <ChangelogTimeline />
        </MobileContent>
      </>
    );
  }

  return (
    <div className="mx-auto my-6 w-full max-w-3xl">
      <header className="mb-6">
        <h1 className="text-3xl font-semibold tracking-tight">
          {translate("crm.changelog.title")}
        </h1>
        <p className="mt-1 text-muted-foreground">
          {translate("crm.changelog.subtitle")}
        </p>
      </header>
      <ChangelogTimeline />
    </div>
  );
};

ChangelogPage.path = "/changelog";
